import { useState, useEffect, useCallback } from 'react';
import type {
  FabricPipelineStatus,
  IPipelineLatestStatus,
  IPipelineRun,
} from '@skystone/shared';
import { pipelineStatusApi } from '@/services/pipeline-status-api';
import { formatDateTime } from '@/lib/date-utils';
import { DataTable, ColumnDef, ServerPaginationConfig } from '@/components/ui/DataTable';
import { Badge, PageHeader, PageLoading, ErrorBanner } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { getErrorMessage } from '@/lib/utils';
import { inputClass } from '@/lib/form-styles';
import { PageContainer } from '@/components/layout';

type StatusBadgeVariant = 'success' | 'error' | 'warning';

const STATUS_BADGE_MAP: Record<FabricPipelineStatus, StatusBadgeVariant> = {
  SUCCESS: 'success',
  FAILED: 'error',
  RUNNING: 'warning',
};

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '\u2014';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remaining}s`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

// -- Shared cell renderers (avoid duplication across column sets) --

function renderStatusBadge(value: unknown) {
  const status = value as FabricPipelineStatus;
  return <Badge variant={STATUS_BADGE_MAP[status] ?? 'secondary'}>{status}</Badge>;
}

function renderTimestamp(value: unknown) {
  return formatDateTime(value as string | null);
}

function renderDuration(value: unknown) {
  return formatDuration(value as number | null);
}

function renderNullable(value: unknown) {
  return (value as string | null) ?? '\u2014';
}

// -- Latest Status columns (defined outside component per S7721) --

const latestStatusColumns: ColumnDef<IPipelineLatestStatus>[] = [
  { id: 'pipelineName', header: 'Pipeline', accessor: 'pipelineName', sortable: true },
  { id: 'status', header: 'Status', accessor: 'status', cell: renderStatusBadge },
  { id: 'runDate', header: 'Run Date', accessor: 'runDate', sortable: true },
  { id: 'startedAt', header: 'Started', accessor: 'startedAt', cell: renderTimestamp },
  { id: 'finishedAt', header: 'Finished', accessor: 'finishedAt', cell: renderTimestamp },
  { id: 'duration', header: 'Duration', accessor: 'durationSeconds', cell: renderDuration },
  { id: 'failedActivity', header: 'Failed Activity', accessor: 'failedActivity', cell: renderNullable },
];

// -- History columns (defined outside component per S7721) --

const historyColumns: ColumnDef<IPipelineRun>[] = [
  { id: 'pipelineName', header: 'Pipeline', accessor: 'pipelineName' },
  { id: 'status', header: 'Status', accessor: 'status', cell: renderStatusBadge },
  { id: 'runDate', header: 'Run Date', accessor: 'runDate' },
  { id: 'startedAt', header: 'Started', accessor: 'startedAt', cell: renderTimestamp },
  { id: 'finishedAt', header: 'Finished', accessor: 'finishedAt', cell: renderTimestamp },
  { id: 'duration', header: 'Duration', accessor: 'durationSeconds', cell: renderDuration },
  { id: 'failedActivity', header: 'Failed Activity', accessor: 'failedActivity', cell: renderNullable },
  {
    id: 'errorMsg',
    header: 'Error',
    accessor: 'errorMsg',
    cell: (value) => {
      const msg = value as string | null;
      if (!msg) return '\u2014';
      const truncated = msg.length > 120 ? `${msg.substring(0, 120)}...` : msg;
      return <span title={msg}>{truncated}</span>;
    },
  },
];

export function PipelineStatusPage() {
  const { data: latestStatus, loading: latestLoading, error: latestError } = useApi(
    () => pipelineStatusApi.getLatestStatus(),
    [],
  );

  const [pipelineNames, setPipelineNames] = useState<string[]>([]);
  const [filterPipeline, setFilterPipeline] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [historyRuns, setHistoryRuns] = useState<IPipelineRun[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(25);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    pipelineStatusApi.getPipelineNames().then(setPipelineNames).catch(() => { /* non-critical */ });
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const result = await pipelineStatusApi.getRunHistory({
        pipelineName: filterPipeline || undefined,
        status: filterStatus || undefined,
        page: historyPage,
        pageSize: historyPageSize,
      });
      setHistoryRuns(result.runs);
      setHistoryTotal(result.total);
    } catch (err: unknown) {
      setHistoryError(getErrorMessage(err, 'Failed to load run history'));
    } finally {
      setHistoryLoading(false);
    }
  }, [filterPipeline, filterStatus, historyPage, historyPageSize]);

  useEffect(() => {
    loadHistory().catch(() => { /* handled inside loadHistory */ });
  }, [loadHistory]);

  const handlePageChange = (page: number): void => {
    setHistoryPage(page);
  };

  const handlePageSizeChange = (size: number): void => {
    setHistoryPageSize(size);
    setHistoryPage(1);
  };

  const handleFilterChange = (): void => {
    setHistoryPage(1);
  };

  const serverPagination: ServerPaginationConfig = {
    page: historyPage,
    pageSize: historyPageSize,
    total: historyTotal,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
  };

  if (latestLoading) return <PageLoading />;

  return (
    <PageContainer variant="wide" className="space-y-8">
      <PageHeader
        title="Pipeline Status"
        subtitle="Fabric pipeline execution status and run history."
      />

      <ErrorBanner message={latestError} />

      {/* Latest Status */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Latest Status</h2>
        <DataTable
          data={latestStatus ?? []}
          columns={latestStatusColumns}
          keyExtractor={(row) => row.pipelineName}
          emptyMessage="No pipeline data available."
        />
      </section>

      {/* Run History */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Run History</h2>

        <div className="flex gap-3 items-end">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground" htmlFor="filter-pipeline">
              Pipeline
            </label>
            <select
              id="filter-pipeline"
              className={inputClass}
              value={filterPipeline}
              onChange={(e) => { setFilterPipeline(e.target.value); handleFilterChange(); }}
            >
              <option value="">All pipelines</option>
              {pipelineNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground" htmlFor="filter-status">
              Status
            </label>
            <select
              id="filter-status"
              className={inputClass}
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); handleFilterChange(); }}
            >
              <option value="">All statuses</option>
              <option value="SUCCESS">SUCCESS</option>
              <option value="FAILED">FAILED</option>
              <option value="RUNNING">RUNNING</option>
            </select>
          </div>
        </div>

        <ErrorBanner message={historyError} />

        <DataTable
          data={historyRuns}
          columns={historyColumns}
          keyExtractor={(row) => row.id}
          isLoading={historyLoading}
          serverPagination={serverPagination}
          emptyMessage="No pipeline runs found."
        />
      </section>
    </PageContainer>
  );
}
